import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Play, X } from 'lucide-react';
import { CrisisFooter } from '@/components/CrisisFooter';
import { logError } from '@/lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEATHER_OPTIONS = [
  { id: 'stormy', icon: '🌩', label: 'Stormy' },
  { id: 'heavy', icon: '🌧', label: 'Heavy' },
  { id: 'cloudy', icon: '⛅', label: 'In between' },
  { id: 'lifting', icon: '🌤', label: 'Lifting' },
  { id: 'grounded', icon: '☀️', label: 'Grounded' },
];

const BREATHWORK_OPTIONS = [
  {
    id: '478',
    title: '4-7-8 BREATHING',
    description: 'For anxiety and overwhelm',
    cycles: 4,
    phases: [
      { name: 'Inhale', duration: 4, color: '#C9A84C', instruction: 'Breathe in through your nose' },
      { name: 'Hold', duration: 7, color: '#A9756B', instruction: 'Hold gently' },
      { name: 'Exhale', duration: 8, color: '#7d9d8c', instruction: 'Release slowly through your mouth' },
    ],
    completionMessage: 'Your nervous system just received a signal that you are safe. Well done.',
  },
  {
    id: 'box',
    title: 'BOX BREATHING',
    description: 'For focus and grounding',
    cycles: 4,
    phases: [
      { name: 'Inhale', duration: 4, color: '#C9A84C', instruction: 'Breathe in' },
      { name: 'Hold', duration: 4, color: '#A9756B', instruction: 'Hold' },
      { name: 'Exhale', duration: 4, color: '#7d9d8c', instruction: 'Breathe out' },
      { name: 'Hold', duration: 4, color: '#9d8ca4', instruction: 'Hold empty' },
    ],
    completionMessage: 'You just regulated your stress response. Your body knows how to heal.',
  },
  {
    id: 'release',
    title: 'RELEASING BREATH',
    description: 'For grief and heaviness',
    cycles: 6,
    phases: [
      { name: 'Inhale', duration: 5, color: '#C9A84C', instruction: 'Breathe in through your nose' },
      { name: 'Exhale', duration: 8, color: '#7d9d8c', instruction: 'Long exhale through your mouth' },
    ],
    completionMessage: 'Grief moves through the body on the exhale. You just gave it somewhere to go.',
  },
];

// Breathing Circle Animation
const BreathingCircle = ({ phase, isActive, exerciseId }) => {
  const getScale = () => {
    if (!isActive) return 1;
    if (phase?.name === 'Inhale') return 1.4;
    if (phase?.name === 'Exhale') return 0.8;
    return 1.1; // Hold
  };

  // Helper for release breath wave transform
  const getWaveTransform = () => {
    if (!isActive) return 'scaleX(1) scaleY(1)';
    const isInhale = phase?.name === 'Inhale';
    const scaleX = isInhale ? 1.3 : 0.7;
    const scaleY = isInhale ? 0.8 : 1.2;
    return `scaleX(${scaleX}) scaleY(${scaleY})`;
  };

  // Box breathing uses square, others use circle
  if (exerciseId === 'box') {
    return (
      <div className="relative w-48 h-48 mx-auto">
        <div
          className="absolute inset-0 rounded-xl transition-all ease-in-out"
          style={{
            backgroundColor: phase?.color || 'rgba(201,168,76,0.2)',
            opacity: isActive ? 0.3 : 0.1,
            transform: `scale(${getScale()})`,
            transitionDuration: phase ? `${phase.duration}s` : '0.5s',
            boxShadow: isActive ? `0 0 60px ${phase?.color}40` : 'none',
          }}
        />
        <div
          className="absolute inset-4 rounded-lg border-2 transition-all ease-in-out flex items-center justify-center"
          style={{
            borderColor: phase?.color || '#C9A84C',
            transform: `scale(${getScale()})`,
            transitionDuration: phase ? `${phase.duration}s` : '0.5s',
          }}
        >
          <span className="text-lg font-medium" style={{ color: phase?.color || '#C9A84C' }}>
            {phase?.name || 'Ready'}
          </span>
        </div>
      </div>
    );
  }

  // Release breath uses wave animation
  if (exerciseId === 'release') {
    return (
      <div className="relative w-48 h-48 mx-auto flex items-center justify-center">
        <div
          className="absolute w-full h-24 rounded-full transition-all ease-in-out"
          style={{
            backgroundColor: phase?.color || 'rgba(201,168,76,0.2)',
            opacity: isActive ? 0.4 : 0.1,
            transform: getWaveTransform(),
            transitionDuration: phase ? `${phase.duration}s` : '0.5s',
            boxShadow: isActive ? `0 0 40px ${phase?.color}30` : 'none',
          }}
        />
        <span className="relative z-10 text-lg font-medium" style={{ color: phase?.color || '#C9A84C' }}>
          {phase?.name || 'Ready'}
        </span>
      </div>
    );
  }

  // Default circle for 4-7-8
  return (
    <div className="relative w-48 h-48 mx-auto">
      <div
        className="absolute inset-0 rounded-full transition-all ease-in-out"
        style={{
          backgroundColor: phase?.color || 'rgba(201,168,76,0.2)',
          opacity: isActive ? 0.3 : 0.1,
          transform: `scale(${getScale()})`,
          transitionDuration: phase ? `${phase.duration}s` : '0.5s',
          boxShadow: isActive ? `0 0 60px ${phase?.color}40` : 'none',
        }}
      />
      <div
        className="absolute inset-4 rounded-full border-2 transition-all ease-in-out flex items-center justify-center"
        style={{
          borderColor: phase?.color || '#C9A84C',
          transform: `scale(${getScale()})`,
          transitionDuration: phase ? `${phase.duration}s` : '0.5s',
        }}
      >
        <span className="text-lg font-medium" style={{ color: phase?.color || '#C9A84C' }}>
          {phase?.name || 'Ready'}
        </span>
      </div>
    </div>
  );
};

// Exercise Card
const ExerciseCard = ({ exercise, onStart }) => (
  <button
    onClick={() => onStart(exercise)}
    className="w-full p-6 rounded-2xl text-left transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
    style={{
      background: '#F9F4F0',
      border: '1px solid #F0EBE4',
    }}
    data-testid={`exercise-${exercise.id}`}
  >
    <h3 className="text-xs tracking-[0.15em] mb-2" style={{ color: '#C9A84C' }}>
      {exercise.title}
    </h3>
    <p className="text-sm" style={{ color: '#4A4A5A' }}>
      {exercise.description}
    </p>
    <div className="flex items-center gap-2 mt-4">
      <Play className="w-4 h-4" style={{ color: '#C9A84C' }} />
      <span className="text-xs" style={{ color: '#8A8798' }}>
        {exercise.cycles} cycles
      </span>
    </div>
  </button>
);

// Active Exercise View
const ActiveExercise = ({ exercise, onComplete, onCancel }) => {
  const [currentCycle, setCurrentCycle] = useState(1);
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const timerRef = useRef(null);

  const currentPhase = exercise.phases[currentPhaseIndex];

  const startExercise = useCallback(() => {
    setIsActive(true);
    setCountdown(exercise.phases[0].duration);
    setCurrentPhaseIndex(0);
    setCurrentCycle(1);
  }, [exercise.phases]);

  useEffect(() => {
    if (!isActive || isComplete) return;

    if (countdown > 0) {
      timerRef.current = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else {
      // Move to next phase
      const nextPhaseIndex = currentPhaseIndex + 1;
      
      if (nextPhaseIndex >= exercise.phases.length) {
        // End of cycle
        if (currentCycle >= exercise.cycles) {
          // Exercise complete
          setIsComplete(true);
          setIsActive(false);
        } else {
          // Start next cycle
          setCurrentCycle(currentCycle + 1);
          setCurrentPhaseIndex(0);
          setCountdown(exercise.phases[0].duration);
        }
      } else {
        // Next phase in same cycle
        setCurrentPhaseIndex(nextPhaseIndex);
        setCountdown(exercise.phases[nextPhaseIndex].duration);
      }
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [countdown, isActive, isComplete, currentPhaseIndex, currentCycle, exercise]);

  if (isComplete) {
    return (
      <div className="text-center animate-fadeIn">
        <div className="mb-8">
          <div
            className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
            style={{
              background: 'radial-gradient(circle, rgba(201,168,76,0.4) 0%, rgba(201,168,76,0.1) 100%)',
              boxShadow: '0 0 40px rgba(201,168,76,0.3)',
            }}
          >
            <span className="text-3xl">✨</span>
          </div>
          <p
            className="text-lg md:text-xl leading-relaxed max-w-sm mx-auto"
            style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}
          >
            {exercise.completionMessage}
          </p>
        </div>
        <Button
          onClick={onComplete}
          className="rounded-full px-8 py-6"
          style={{ backgroundColor: '#C9A84C', color: '#FBF9F6' }}
          data-testid="complete-button"
        >
          Continue
        </Button>
      </div>
    );
  }

  return (
    <div className="text-center">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <button
          onClick={onCancel}
          className="p-2 rounded-full hover:bg-[#F7F1EE] transition-colors"
        >
          <X className="w-5 h-5" style={{ color: '#6B6878' }} />
        </button>
        <h2 className="text-xs tracking-[0.15em]" style={{ color: '#C9A84C' }}>
          {exercise.title}
        </h2>
        <div className="w-9" />
      </div>

      {/* Cycle indicator */}
      <p className="text-xs mb-8" style={{ color: '#8A8798' }}>
        Cycle {currentCycle} of {exercise.cycles}
      </p>

      {/* Breathing Animation */}
      <div className="mb-8">
        <BreathingCircle
          phase={isActive ? currentPhase : null}
          isActive={isActive}
          exerciseId={exercise.id}
        />
      </div>

      {/* Instruction */}
      {isActive && (
        <div className="mb-8 animate-fadeIn">
          <p className="text-lg mb-2" style={{ fontFamily: 'Playfair Display, serif', color: currentPhase.color }}>
            {currentPhase.instruction}
          </p>
          <p className="text-3xl font-light" style={{ color: '#1A1A2E' }}>
            {countdown}
          </p>
        </div>
      )}

      {/* Start button */}
      {!isActive && (
        <Button
          onClick={startExercise}
          className="rounded-full px-8 py-6"
          style={{ backgroundColor: '#C9A84C', color: '#FBF9F6' }}
          data-testid="start-exercise-button"
        >
          <Play className="w-4 h-4 mr-2" />
          Begin
        </Button>
      )}
    </div>
  );
};

// Post-Exercise Mood Check
const PostExerciseMood = ({ navigate, token }) => {
  const [selectedWeather, setSelectedWeather] = useState(null);

  const handleSelect = async (weather) => {
    setSelectedWeather(weather.id);
    
    try {
      await axios.post(
        `${API}/mood/save`,
        { mood: weather.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      // Mood save failure is non-critical
      logError('Mood save failed:', err?.response?.status);
    }
  };

  const handleTalkToSage = () => {
    navigate('/chat', { state: { preloadedMessage: "I just did a breathing exercise and I'm feeling..." } });
  };

  return (
    <div className="text-center animate-fadeIn">
      <h2 className="text-xl md:text-2xl mb-8" style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}>
        How do you feel now?
      </h2>

      <div className="flex justify-center gap-4 mb-10 flex-wrap">
        {WEATHER_OPTIONS.map((weather) => (
          <button
            key={weather.id}
            onClick={() => handleSelect(weather)}
            className="flex flex-col items-center transition-all duration-300"
            data-testid={`post-weather-${weather.id}`}
          >
            <div
              className={`w-14 h-14 rounded-full flex items-center justify-center text-xl mb-2 transition-all duration-300 ${
                selectedWeather === weather.id ? 'scale-110' : 'hover:scale-105'
              }`}
              style={{
                background: selectedWeather === weather.id
                  ? 'radial-gradient(circle, rgba(201,168,76,0.4) 0%, rgba(201,168,76,0.1) 100%)'
                  : 'radial-gradient(circle, rgba(232,180,184,0.15) 0%, rgba(232,180,184,0.05) 100%)',
                boxShadow: selectedWeather === weather.id ? '0 0 20px rgba(201,168,76,0.4)' : 'none',
              }}
            >
              {weather.icon}
            </div>
            <span
              className="text-xs transition-colors duration-300"
              style={{ color: selectedWeather === weather.id ? '#C9A84C' : '#8A8798' }}
            >
              {weather.label}
            </span>
          </button>
        ))}
      </div>

      <div className="space-y-3">
        <Button
          onClick={handleTalkToSage}
          className="w-full max-w-xs rounded-full px-8 py-6"
          style={{ backgroundColor: '#C9A84C', color: '#FBF9F6' }}
          data-testid="talk-to-sage-button"
        >
          Talk to Sage about this
        </Button>
        
        <Button
          onClick={() => navigate('/sanctuary')}
          variant="outline"
          className="w-full max-w-xs rounded-full px-8 py-6"
          style={{ borderColor: '#D5CFC6', color: '#4A4A5A' }}
          data-testid="return-sanctuary-button"
        >
          Return to Sanctuary
        </Button>
      </div>
    </div>
  );
};

export const BreathePage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [exerciseComplete, setExerciseComplete] = useState(false);

  const handleStartExercise = (exercise) => {
    setSelectedExercise(exercise);
    setExerciseComplete(false);
  };

  const handleExerciseComplete = () => {
    setExerciseComplete(true);
  };

  const handleCancel = () => {
    setSelectedExercise(null);
    setExerciseComplete(false);
  };

  const handleBack = () => {
    if (selectedExercise && !exerciseComplete) {
      setSelectedExercise(null);
    } else {
      navigate('/sanctuary');
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#FBF9F6' }}>
      {/* Header - only show when no exercise is active */}
      {(!selectedExercise || exerciseComplete) && (
        <header className="p-6">
          <div className="max-w-md mx-auto flex items-center justify-between">
            <button
              onClick={handleBack}
              className="p-2 rounded-full transition-colors hover:bg-[#F7F1EE]"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" style={{ color: '#6B6878' }} />
            </button>
            <div className="text-center">
              <h1
                className="text-2xl font-medium"
                style={{ fontFamily: 'Playfair Display, serif', color: '#C9A84C' }}
              >
                Return to your body
              </h1>
              <p className="text-sm" style={{ color: '#6B6878' }}>
                Choose your practice
              </p>
            </div>
            <div className="w-9" />
          </div>
        </header>
      )}

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 pb-24">
        <div className="w-full max-w-md">
          {!selectedExercise && (
            <div className="space-y-4 animate-fadeIn">
              {BREATHWORK_OPTIONS.map((exercise) => (
                <ExerciseCard
                  key={exercise.id}
                  exercise={exercise}
                  onStart={handleStartExercise}
                />
              ))}
            </div>
          )}

          {selectedExercise && !exerciseComplete && (
            <ActiveExercise
              exercise={selectedExercise}
              onComplete={handleExerciseComplete}
              onCancel={handleCancel}
            />
          )}

          {exerciseComplete && (
            <PostExerciseMood navigate={navigate} token={token} />
          )}
        </div>
      </main>

      {/* Crisis Footer */}
      <CrisisFooter />

      {/* CSS for animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};

export default BreathePage;
